import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {
  SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_SAMPLED_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {MAX_DATA_POINTS} from 'analysis-tool/charting/AnalysisChartingConstants';
import {
  findAndAssertInputStepData,
  findAndAssertOutputStepData,
  findAndAssertSingleStepDataFrameId,
  findSingleStepOutputDataFrameId,
} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import getStepVisualizationConfigAndName from 'analysis-tool/data/getVisualizationConfigAndName';
import QueryingChartAndDataFrame from 'analysis-tool/ui/main-view/QueryingChartAndDataFrame';
import {assert} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import DataFrameWarningBanner from 'data-frames/graphql/DataFrameWarningBanner';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {assertIsAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import {BlockViewContext} from 'insights/universal-blocks/BlockViewContext';
import PresentationalUniversalBlockView from 'insights/universal-blocks/PresentationalUniversalBlockView';
import ToggleExpandButton from 'ui-library/buttons/ToggleExpandButton';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Pane from 'ui-library/pane/Pane';

export interface DashboardVisualizationBlockProps {
  banner?: React.ReactNode;
  clientStateId: string;
  isExpanded: boolean;
  showLoadingBlock?: boolean;
  onToggleExpand: (newIsExpanded: boolean) => void;
  step: GetAnalysisPipelineStep;
  tooltip?: string;
}

/**
 * This component will render a loading block if:
 *   - The step doesn't have input data frames set, i.e. it hasn't been successfully run yet
 *   - The showLoadingBlock prop is true
 *   - While the step's data frames and chart are being fetched by GraphQL.
 *
 * Otherwise, it will try to render the step's associated chart/table.
 */
export default function DashboardVisualizationBlock({
  banner,
  clientStateId,
  isExpanded,
  showLoadingBlock,
  onToggleExpand,
  step: chartOrTableStep,
  tooltip,
}: DashboardVisualizationBlockProps): JSX.Element {
  const {visualizationConfig, viewName} = getStepVisualizationConfigAndName(chartOrTableStep);
  const visualizationType = getVisualizationType(visualizationConfig);

  if (
    findAndAssertInputStepData(chartOrTableStep, SINGLE_DATASET_INPUT_STEP_DATA_NAME).dataFrames.length ===
      0 ||
    showLoadingBlock
  ) {
    return (
      <LoadingBlock banner={banner} clientStateId={clientStateId} tooltip={tooltip} viewName={viewName} />
    );
  }

  const chartId =
    chartOrTableStep.statusCode === PipelineStepStatusCode.SUCCEEDED &&
    chartOrTableStep.definition.stepType === PipelineStepType.CHART_VIEW
      ? assertIsAccessible(
          findAndAssertOutputStepData(chartOrTableStep, SINGLE_CHART_OUTPUT_STEP_DATA_NAME).charts[0]
        ).id
      : undefined;

  const inputDataFrameId = findAndAssertSingleStepDataFrameId(
    chartOrTableStep,
    SINGLE_DATASET_INPUT_STEP_DATA_NAME,
    true
  );

  const sampledOutputDataFrameId =
    chartOrTableStep.statusCode === PipelineStepStatusCode.FAILED || PipelineStepType.TABLE_VIEW
      ? null
      : findSingleStepOutputDataFrameId(chartOrTableStep, SINGLE_DATASET_SAMPLED_OUTPUT_STEP_DATA_NAME);

  return (
    <QueryingChartAndDataFrame
      chartId={chartId}
      inputDataFrameId={sampledOutputDataFrameId ?? inputDataFrameId}
      fullDataFrameId={inputDataFrameId}
      renderLoading={() => (
        <LoadingBlock banner={banner} clientStateId={clientStateId} tooltip={tooltip} viewName={viewName} />
      )}
    >
      {({dataFrame, numRows, chart}) => {
        assert(!isInaccessible(dataFrame), 'User does not have access to step input');

        return (
          <PresentationalUniversalBlockView
            actionOrigin={ActionOrigin.MACHINE_LEARNING}
            renderFromChart={visualizationType !== VisualizationType.TABLE}
            chart={chart}
            inputDataFrame={dataFrame.dataFrameJson}
            viewStateId={clientStateId}
            visualizationConfig={visualizationConfig}
            maxRows={MAX_DATA_POINTS}
            paneProps={{
              banner: (
                <DataFrameWarningBanner
                  allColumnsJson={dataFrame.allColumnsJson}
                  dataFrame={dataFrame.dataFrameJson}
                  isRandomSubset={false}
                  name={viewName}
                  totalRows={numRows}
                />
              ),
              description: tooltip,
              title: viewName,
              toolbar: (
                <div className={css(styles.toolbar)}>
                  {banner}
                  <ToggleExpandButton isExpanded={isExpanded} onChange={onToggleExpand} />
                </div>
              ),
            }}
          />
        );
      }}
    </QueryingChartAndDataFrame>
  );
}

const styles = StyleSheet.create({
  toolbar: {display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 10},
});

function LoadingBlock({
  banner,
  clientStateId,
  tooltip,
  viewName,
}: Pick<DashboardVisualizationBlockProps, 'banner' | 'clientStateId' | 'tooltip'> & {
  viewName: string;
}): JSX.Element {
  const blockContext = useMemo(
    () => ({actionOrigin: ActionOrigin.MACHINE_LEARNING, viewStateId: clientStateId}),
    [clientStateId]
  );
  return (
    <BlockViewContext.Provider value={blockContext}>
      <Pane
        centerContent={false}
        description={tooltip}
        title={viewName}
        toolbar={<div className={css(styles.toolbar)}>{banner}</div>}
      >
        <CenteredHorizontalThrobber />
      </Pane>
    </BlockViewContext.Provider>
  );
}
